import { useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { createLayoutResizeObserver } from '@features/shared/utils/layoutResizeObserver'
import { measureRailAnchor, RAIL_MODULE_ATTRIBUTE } from '../logic/railAnchor'
import { placeRailCard } from '../logic/railCard'
import { RAIL_FLYOUT_CARD_CLASS, RAIL_FLYOUT_LEFT_CLASS } from './ModuleFlyout'

/** A rail item without a menu, by its row's RAIL_MODULE_ATTRIBUTE value, and its name. */
export interface RailName {
  anchor: string
  label: string
}

/** On the name card that takes the pointer, so its item knows the pointer went there. */
export const RAIL_NAME_CARD_ATTRIBUTE = 'data-rail-name-card'

/**
 * The name of a rail item that has no menu (Home, AI; Search while the rail
 * shows only icons): a card where an area's menu would open, over a pinned
 * one (blank meanwhile), centred on the item's icon. It's an item's row tall
 * plus a group's line above and below it, so on the rail's first item its
 * top meets a menu card's. It shows at once and fades on leaving. The pointer may cross to
 * it, and it stays while the pointer is on it, until it leaves for anywhere
 * but its item (`onLeave`); a click on it is a click on its item (Home opens
 * Home), for whoever clicks the name rather than the icon. It may say more
 * (`detail`, after a line: Home's page, with a pencil to change it), whose
 * own controls keep their clicks. The link is named by its own label, so a
 * card saying nothing more is only seen, not read.
 */
export function RailNameCard({
  name,
  details,
  onEnter,
  onLeave,
}: {
  name: RailName | null
  /** What an item's card says after its name, by its anchor (Home's page). */
  details?: Readonly<Partial<Record<string, ReactNode>>>
  onEnter?: () => void
  onLeave?: () => void
}) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  // The last name shown, kept while the card fades out.
  const [shown, setShown] = useState<RailName | null>(name)
  if (name && (name.anchor !== shown?.anchor || name.label !== shown?.label)) setShown(name)
  const [top, setTop] = useState(0)

  const anchor = shown?.anchor ?? null
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    const card = cardRef.current
    const layer = wrapper?.offsetParent
    if (!wrapper || !card || !(layer instanceof HTMLElement) || !anchor) return

    const place = () => {
      const { tileCenter, top: minTop, room } = measureRailAnchor(layer, anchor)
      if (tileCenter === null) return
      const desiredTop = tileCenter - card.offsetHeight / 2
      const bottomLimit = minTop + Math.max(0, room - card.offsetHeight)
      const next = placeRailCard(desiredTop, bottomLimit, minTop)
      setTop((current) => (current === next ? current : next))
    }

    place()
    // Placed before the browser paints, so it never shows in the wrong spot.
    const observer = createLayoutResizeObserver(place)
    observer.observe(card)
    observer.observe(layer)
    return () => observer.disconnect()
  }, [anchor, shown?.label])

  if (!shown) return null
  // Its own while it fades out too, so it never shrinks on the way.
  const detail = details?.[shown.anchor]
  const hasDetail = detail != null
  const itemSelector = `[${RAIL_MODULE_ATTRIBUTE}="${shown.anchor}"]`

  // The card stands for its item: a click on it (but on a control of its
  // own, as Home's pencil) clicks the item.
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest('button, a, input')) return
    document.querySelector<HTMLElement>(itemSelector)?.click()
  }

  const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    const to = event.relatedTarget
    // Back onto its item: still named there.
    if (to instanceof Element && to.closest(itemSelector)) return
    onLeave?.()
  }

  return (
    <div
      ref={wrapperRef}
      aria-hidden={hasDetail ? undefined : true}
      {...{ [RAIL_NAME_CARD_ATTRIBUTE]: '' }}
      // Show together with the pinned menu's idle veil; only fade on leaving.
      // Its gap from the rail belongs to it, so crossing never leaves it.
      data-shown={name !== null}
      className={`rail-flyout-fade rail-flyout-instant absolute ${RAIL_FLYOUT_LEFT_CLASS} z-10`}
      style={{ top }}
      onMouseEnter={onEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* The rail item it names is the keyboard's way there; this is the pointer's. */}
      <div
        ref={cardRef}
        onClick={handleClick}
        // An item's row plus a group's line (and its inset) above and below.
        className={`flex h-[calc(var(--sidebar-tile-size)+2*(var(--sidebar-item-inset)+1px))] w-max max-w-[24rem] cursor-pointer items-center gap-2 whitespace-nowrap px-3 text-sm font-medium text-[color:var(--sidebar-submenu-text-default)] ${RAIL_FLYOUT_CARD_CLASS}`}
      >
        {shown.label}
        {hasDetail ? (
          <>
            <span
              aria-hidden="true"
              className="h-sidebar-module-size w-px shrink-0 bg-[color:var(--border-strong)]"
            />
            <span className="-mr-1.5 flex min-w-0 items-center gap-1 font-normal">{detail}</span>
          </>
        ) : null}
      </div>
    </div>
  )
}
